import { uncapitalize } from '@obvious.tech/util/string'
import createRequest from './createRequest'
import {
  TParsedConstellationSwaggerPathParameters,
  TParsedConstellationSwaggerPathResponses,
  TParsedConstellationSwaggerPaths,
  TParsers,
} from '../types'
import { OpenAPIV2 } from 'openapi-types'
import {
  followReference,
  getReferenceKey,
  isReference,
} from '../util/reference'

const ParseSwaggerPathResponses = ({
  responses = {},
  entity = 'unknown',
  fctName,
}): TParsedConstellationSwaggerPathResponses => {
  return Object.keys(responses).map(responseKey => {
    const type = fctName === 'getAll' ? 'array' : 'object'

    return {
      name: responseKey,
      type,
      entity: Number(responseKey) < 400 ? entity : 'ErrorResponse',
    }
  })
}

const ParseSwaggerPathParameters = (
  parameters: Array<OpenAPIV2.Parameter | OpenAPIV2.ReferenceObject> = [],
  swaggerfiles: OpenAPIV2.Document,
): TParsedConstellationSwaggerPathParameters => {
  return parameters
    .map(followReference(swaggerfiles)) //
    .map(parameter => {
      return {
        name: parameter.name,
        type: 'type' in parameter ? parameter.type : 'object',
        entity:
          ('name' in parameter && parameter.name === 'entity') ||
          (!('type' in parameter) && isReference(parameter.schema))
            ? getReferenceKey(parameter.schema)
            : undefined,
        required: parameter.required ?? false,
      }
    })
}

const PathsParser: TParsers['paths'] = (swaggerfiles, baseUrl) => {
  const out: TParsedConstellationSwaggerPaths = {}

  Object.keys(swaggerfiles.paths).forEach(pathKey => {
    Object.keys(swaggerfiles.paths[pathKey]).forEach(actionType => {
      const operationId = swaggerfiles.paths[pathKey][actionType].operationId
      const delimiterIndex = operationId.indexOf('_')
      const entityName = operationId.substring(0, delimiterIndex)
      const fctName = uncapitalize(operationId.substring(delimiterIndex + 1))
      const outEntity = (out[entityName] ??= {
        functions: {},
      })
      if (pathKey.split('/')[1] !== 'api') {
        outEntity.microservice = pathKey.split('/')[1]
        outEntity.functions = {
          ...outEntity.functions,
          [fctName]: {
            microservice: pathKey.split('/')[1],
            name: fctName,
            parameters: ParseSwaggerPathParameters(
              swaggerfiles.paths[pathKey][actionType].parameters,
              swaggerfiles,
            ),
            responses: ParseSwaggerPathResponses({
              responses: swaggerfiles.paths[pathKey][actionType].responses,
              entity: entityName,
              fctName,
            }),
            call: async (...args) => {
              const { url, options } = createRequest(
                swaggerfiles,
                actionType,
                baseUrl,
                pathKey,
                swaggerfiles.paths[pathKey][actionType],
              )(...args)
              return await fetch(url, options)
                .then(async res => {
                  return await processResponse(res)
                })
                .catch(() => {})
            },
          },
        }
        return
      }

      out.custom ??= {}
      const path = swaggerfiles.paths[pathKey][actionType]
      out.custom[pathKey] = {
        ...out.custom[pathKey],
        [fctName]: {
          name: fctName,
          responses: ParseSwaggerPathResponses({
            responses: path.responses,
            entity: pathKey,
            fctName,
          }),
          parameters: ParseSwaggerPathParameters(path.parameters, swaggerfiles),
          call: async (...args: unknown[]) => {
            const { url, options } = createRequest(
              swaggerfiles,
              actionType,
              baseUrl,
              pathKey,
              swaggerfiles.paths[pathKey][actionType],
            )(...args)
            return await fetch(url, options)
              .then(async res => {
                return await processResponse(res)
              })
              .catch(() => {})
          },
        },
      }
    })
  })
  return out
}

const processResponse = async (response: Response): Promise<unknown> => {
  return JSON.parse(await response.text())
}

export default PathsParser
